import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rulesApi } from "../api/rules.api.js";
import { useAuth } from "../auth/useAuth.js";
import { useToast } from "../components/ui/ToastProvider.js";
import { BackLink, Card, EmptyState, ErrorState, Icon, PageHeader, Pill, Section, SkeletonList, StatusChip, colors, } from "../components/ui/index.js";
import { addMinutes, formatTimeRange } from "../lib/format-session.js";
import { venueShortName } from "../lib/venue-names.js";
import { humanizeAuditEntry } from "../lib/humanize-log.js";
import { bulkBlocksOverlapping, maxBookableAt, blockedCourtCount, overlapDates, } from "../components/rules/bulk-warning.js";
import { BulkBookingGrid } from "../components/rules/BulkBookingGrid.js";
const DAY_LABEL = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];
function formatSessionDate(iso) {
    return new Date(iso).toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
    });
}
export function RuleDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const { showToast } = useToast();
    const qc = useQueryClient();
    const [showAllAudit, setShowAllAudit] = useState(false);
    const q = useQuery({
        queryKey: ["rule", id],
        queryFn: () => rulesApi.detail(id ?? ""),
        enabled: Boolean(id),
    });
    const ruleVenueId = q.data?.rule.venueId;
    const bulkQuery = useQuery({
        queryKey: ["sidecar", "bulk-bookings", ruleVenueId],
        queryFn: () => rulesApi.bulkBookings(ruleVenueId ?? ""),
        enabled: Boolean(ruleVenueId),
        staleTime: 30 * 60 * 1000,
    });
    const createNext = useMutation({
        mutationFn: (ruleId) => rulesApi.createNextSession(ruleId),
        onSuccess: (data) => {
            const msg = data.createdCount > 0
                ? `Created the next session${data.armedCount > 0 ? " and armed it" : ""}.`
                : data.armedCount > 0
                    ? `Next session already existed — armed its booking.`
                    : `Nothing to do — the next session already exists and is armed.`;
            showToast(msg, "success");
            void qc.invalidateQueries({ queryKey: ["rule", id] });
            void qc.invalidateQueries({ queryKey: ["sessions"] });
        },
        onError: (err) => {
            showToast(err instanceof Error ? err.message : "Create next session failed", "error");
        },
    });
    if (q.isLoading)
        return _jsx(SkeletonList, { rows: 4 });
    if (q.error || !q.data)
        return _jsx(ErrorState, { message: "Failed to load preference" });
    const { rule, sessions, audit } = q.data;
    // The block-booking overlap warning is best-effort. If `bulkQuery` errors or
    // is still loading we intentionally render NOTHING extra (no error/loading
    // surface): `bulkDayBlocks` falls back to [], `bulkOverlap` stays empty, and
    // the warning Card simply doesn't appear. This silent degradation is by
    // design — a transient bulk-bookings fetch failure must not block or clutter
    // the preference detail view; the core rule data still renders normally.
    const bulkDayBlocks = bulkQuery.data?.byDay[rule.dayOfWeek] ?? [];
    const bulkOverlap = bulkBlocksOverlapping(bulkDayBlocks, {
        dayOfWeek: rule.dayOfWeek,
        startTime: rule.startTime,
        durationMin: rule.durationMin,
    });
    const bulkMax = maxBookableAt(bulkDayBlocks, { dayOfWeek: rule.dayOfWeek, startTime: rule.startTime, durationMin: rule.durationMin }, bulkQuery.data?.totalCourts ?? 0);
    // The activity log can grow long; show the most recent entries with an
    // expand toggle rather than dumping everything.
    const AUDIT_PREVIEW = 12;
    const sortedAudit = [...audit].sort((a, b) => (a.ts < b.ts ? 1 : -1));
    const visibleAudit = showAllAudit ? sortedAudit : sortedAudit.slice(0, AUDIT_PREVIEW);
    const auditHidden = sortedAudit.length - visibleAudit.length;
    const impersonating = user?.impersonating === true;
    const blocked = Boolean(rule.eligibility && !rule.eligibility.ok);
    const status = !rule.active ? "paused" : blocked ? "blocked" : "active";
    const statusColor = status === "active"
        ? colors.success
        : status === "blocked"
            ? colors.danger
            : colors.textTertiary;
    const nowMs = q.dataUpdatedAt;
    const upcoming = sessions
        .filter((s) => new Date(s.startTime).getTime() >= nowMs)
        .sort((a, b) => (a.startTime < b.startTime ? -1 : 1));
    const past = sessions
        .filter((s) => new Date(s.startTime).getTime() < nowMs)
        .sort((a, b) => (a.startTime > b.startTime ? -1 : 1));
    return (_jsxs("div", { children: [_jsx("div", { style: { marginBottom: 8 }, children: _jsx(BackLink, { to: "/rules", label: "Back to preferences" }) }), _jsx(PageHeader, { title: `${DAY_LABEL[rule.dayOfWeek]} ${rule.startTime}`, subtitle: `${venueShortName(rule.venueId)} · ${rule.courtCount} court${rule.courtCount === 1 ? "" : "s"} · ends ${addMinutes(rule.startTime, rule.durationMin)}`, children: _jsx(Pill, { color: statusColor, children: status === "active" ? "Active" : status === "blocked" ? "Blocked" : "Paused" }) }), bulkOverlap.length > 0 && (_jsxs(Card, { style: { marginBottom: 16, background: colors.warningLight, border: `1px solid ${colors.warning}` }, children: [_jsx("strong", { style: { color: colors.warning }, children: "This preference overlaps a block-booked time." }), _jsxs("p", { style: { margin: "4px 0 8px 0", fontSize: "0.85rem", color: colors.text }, children: ["\u2248", blockedCourtCount(bulkOverlap), " of ", bulkQuery.data?.totalCourts ?? "?", " courts here are block-booked every ", DAY_LABEL[rule.dayOfWeek], " \u2014 this preference will likely get at most", " ", _jsx("strong", { children: bulkMax }), " court", bulkMax === 1 ? "" : "s", "."] }), _jsx(BulkBookingGrid, { dayBlocks: bulkDayBlocks, totalCourts: bulkQuery.data?.totalCourts ?? 0, ruleWindow: {
                            dayOfWeek: rule.dayOfWeek,
                            startTime: rule.startTime,
                            durationMin: rule.durationMin,
                        } }), _jsxs("p", { style: { margin: "8px 0 0 0", fontSize: "0.72rem", color: colors.textTertiary }, children: ["Detected taken on ", overlapDates(bulkOverlap), " (sampled beyond the public booking window)."] })] })), impersonating && (_jsx(Card, { style: {
                    marginBottom: 16,
                    padding: 12,
                    background: colors.warningLight,
                    border: `1px solid ${colors.warningBorder}`,
                }, children: _jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        flexWrap: "wrap",
                    }, children: [_jsxs("div", { style: { fontSize: "0.84rem", color: colors.warningDark }, children: [_jsxs("strong", { children: [_jsx(Icon, { name: "alert-triangle", size: 14 }), " Admin debug"] }), " ", "\u00B7 Create the next session for this preference (the first occurrence ~2 weeks out, as the bot would) and arm its booking if it isn't already."] }), _jsx("button", { type: "button", disabled: createNext.isPending, onClick: () => createNext.mutate(rule.id), style: {
                                background: "transparent",
                                color: colors.warningDark,
                                border: `1px solid ${colors.warningDark}`,
                                padding: "6px 12px",
                                borderRadius: 6,
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                cursor: createNext.isPending ? "wait" : "pointer",
                                opacity: createNext.isPending ? 0.6 : 1,
                            }, children: createNext.isPending ? "Creating…" : "Create next session" })] }) })), _jsx(Section, { title: `Upcoming sessions (${upcoming.length})`, children: upcoming.length === 0 ? (_jsx(EmptyState, { message: "No upcoming sessions yet \u2014 the bot generates these every few hours." })) : (_jsx(Card, { style: { padding: 0, overflow: "hidden" }, children: upcoming.map((s, i) => (_jsxs(Link, { to: `/sessions/${s.id}`, style: {
                            display: "grid",
                            gridTemplateColumns: "minmax(110px, auto) 1fr auto",
                            gap: 12,
                            alignItems: "center",
                            padding: "12px 14px",
                            borderTop: i === 0 ? "none" : `1px solid ${colors.divider}`,
                            textDecoration: "none",
                            color: colors.text,
                            fontSize: "0.85rem",
                        }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column" }, children: [_jsx("span", { style: { fontWeight: 600 }, children: formatSessionDate(s.startTime) }), _jsx("span", { style: {
                                            color: colors.textTertiary,
                                            fontSize: "0.74rem",
                                            fontVariantNumeric: "tabular-nums",
                                        }, children: formatTimeRange(s.startTime, s.endTime) })] }), _jsxs("span", { style: { color: colors.textSecondary, fontVariantNumeric: "tabular-nums" }, children: [s.courts.booked, "/", s.courts.total, " court", s.courts.total === 1 ? "" : "s"] }), _jsx(StatusChip, { status: s.status })] }, s.id))) })) }), past.length > 0 && (_jsx(Section, { title: `Past sessions (${past.length})`, children: _jsx(Card, { style: { padding: 0, overflow: "hidden" }, children: past.slice(0, 10).map((s, i) => (_jsxs(Link, { to: `/sessions/${s.id}`, style: {
                            display: "grid",
                            gridTemplateColumns: "minmax(110px, auto) 1fr auto",
                            gap: 12,
                            alignItems: "center",
                            padding: "10px 14px",
                            borderTop: i === 0 ? "none" : `1px solid ${colors.divider}`,
                            textDecoration: "none",
                            color: colors.textSecondary,
                            fontSize: "0.82rem",
                            opacity: 0.85,
                        }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column" }, children: [_jsx("span", { style: { fontWeight: 600, color: colors.text }, children: formatSessionDate(s.startTime) }), _jsx("span", { style: { color: colors.textTertiary, fontSize: "0.72rem", fontVariantNumeric: "tabular-nums" }, children: formatTimeRange(s.startTime, s.endTime) })] }), _jsxs("span", { style: { fontVariantNumeric: "tabular-nums" }, children: [s.courts.booked, "/", s.courts.total] }), _jsx(StatusChip, { status: s.status })] }, s.id))) }) })), _jsx(Section, { title: `Activity log (${audit.length})`, help: _jsx("p", { style: { margin: 0, fontSize: "0.95rem" }, children: "Everything the bot did for this preference \u2014 sessions created, booked, cancelled, failed." }), children: audit.length === 0 ? (_jsx(EmptyState, { message: "No activity yet for this preference." })) : (_jsxs(Card, { style: { padding: 0, overflow: "hidden" }, children: [visibleAudit.map((e, i) => (_jsxs("div", { style: {
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                gap: 12,
                                padding: "10px 14px",
                                borderTop: i === 0 ? "none" : `1px solid ${colors.divider}`,
                                fontSize: "0.85rem",
                            }, children: [_jsx("span", { children: humanizeAuditEntry(e) }), _jsx("span", { style: {
                                        color: colors.textTertiary,
                                        fontSize: "0.72rem",
                                        whiteSpace: "nowrap",
                                        fontVariantNumeric: "tabular-nums",
                                    }, children: new Date(e.ts).toLocaleString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    }) })] }, e.id))), (auditHidden > 0 || showAllAudit) && (_jsx("button", { type: "button", onClick: () => setShowAllAudit((v) => !v), style: {
                                width: "100%",
                                padding: "10px 14px",
                                border: "none",
                                borderTop: `1px solid ${colors.divider}`,
                                background: colors.surfaceSecondary,
                                color: colors.primary,
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                cursor: "pointer",
                            }, children: showAllAudit ? "Show less" : `Show all ${sortedAudit.length} entries` }))] })) })] }));
}
